import React from 'react';
import { toast } from 'react-toastify';
import type { FieldArrayMethodProps } from 'react-hook-form';

import { ProductEntry, SelectedProduct } from './ProductPriceRangeCreate';

import { TotemSelect } from 'components/TotemSelect';
import { Loader, LoaderModeType } from 'components/Loader';
import { getProductFullName } from 'pages/Products/utils';

import { useGetNonArchivedProductsQuery } from 'data/__generated__';

export const PriceRangeProductSelector = ({
    append,
    selectedProducts,
    setSelectedProducts,
    shouldDisplaySelect = true,
}: {
    append: (value: ProductEntry | ProductEntry[], options?: FieldArrayMethodProps | undefined) => void;
    selectedProducts: SelectedProduct[];
    setSelectedProducts: React.Dispatch<React.SetStateAction<SelectedProduct[]>>;
    shouldDisplaySelect?: boolean;
}) => {
    const { loading: productsLoading, data: productsData, error: productsError } = useGetNonArchivedProductsQuery();

    if (productsLoading) {
        return <Loader mode={LoaderModeType.Spin} />;
    }

    if (productsError || !productsData) {
        toast.error('Une erreur est survenue lors de la récupération des produits non-archivés ');
        return null;
    }

    const { nonArchivedProducts } = productsData;

    const selectedProductIds = selectedProducts.map(({ productId }) => productId);

    const productsOptions =
        nonArchivedProducts
            .filter(({ _id }) => !selectedProductIds.includes(_id))
            .map(({ _id, name, brand, volume }) => ({
                value: _id,
                label: getProductFullName({ name, brand, volume }),
            })) || [];

    const onSelect = (value: string | null) => {
        if (!value) {
            return;
        }
        const option = productsOptions.find((o) => o.value === value);
        if (option && !selectedProducts.some(({ productId }) => productId === value)) {
            setSelectedProducts([...selectedProducts, { productId: value, name: option.label }]);
            append({ productId: value } as ProductEntry);
        }
    };

    return shouldDisplaySelect ? (
        <TotemSelect
            dataTest="select-product"
            label="Ajouter un produit"
            placeholder="Sélectionner un produit (recherche par nom ou par _id)"
            value={null}
            options={productsOptions}
            onChange={onSelect}
        />
    ) : (
        <></>
    );
};
